import type { JSONContent } from '@tiptap/core';
import { shortcodeToEmoji } from '@tiptap/extension-emoji';
import { EMOJIS } from '@examdojo/core/tiptap';

/**
 * Recursively converts emoji nodes to text nodes containing the unicode emoji character.
 * This allows emoji nodes to survive serialization through the MarkdownManager,
 * which doesn't include the Emoji extension.
 */
export function convertEmojiNodesToText(node: JSONContent): JSONContent {
  if (node.type === 'emoji') {
    return convertEmojiNodeToTextNode(node) ?? node;
  }

  if (!node.content) {
    return node;
  }

  return {
    ...node,
    content: convertEmojiNodesInContent(node.content),
  };
}

function convertEmojiNodeToTextNode(node: JSONContent): JSONContent | undefined {
  const name = (node.attrs as { name?: string } | undefined)?.name;
  const emojiItem = name ? shortcodeToEmoji(name, EMOJIS) : undefined;
  const text = emojiItem?.emoji ?? (name ? `:${name}:` : '');

  if (text) {
    return { type: 'text', text, ...(node.marks && { marks: node.marks }) };
  }

  return undefined;
}

function convertEmojiNodesInContent(content: JSONContent[]): JSONContent[] {
  const result: JSONContent[] = [];

  for (const node of content) {
    if (node.type === 'emoji') {
      result.push(convertEmojiNodeToTextNode(node) ?? node);
    } else if (node.content) {
      result.push(convertEmojiNodesToText(node));
    } else {
      result.push(node);
    }
  }

  return result;
}
